import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ease } from '../../core/format/motion';
type Props = {
  show: boolean;
  children: ReactNode;
  /** Toegankelijke naam van de balk (bijv. "Snel naar"). */
  label?: string;
};

/**
 * De zwevende balk onderin op telefoon en tablet: één vorm voor de actiebalk (huidadvies/afspraak,
 * winkelmand), de koopbalk op de productpagina en de betaalbalk bij afrekenen (docs/VORMGEVING.md §4).
 *
 * Gecentreerd met een flex-rij over de volle breedte, niet met `left`/`right` + `margin: auto` op
 * het vaste element zelf: dat laatste rekende op sommige telefoons vanaf een te brede pagina en
 * stond dan rechts. De rij houdt rekening met de veilige zones links, rechts en onder (notch,
 * thuisbalk). Alleen de balk zelf vangt tikken op; de lege rij eromheen laat ze door.
 */
export function FloatingBar({
  show,
  children,
  label
}: Props) {
  const reduce = useReducedMotion();
  return <div className="pointer-events-none fixed inset-x-0 bottom-0 z-bar flex justify-center pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] lg:hidden" data-id="element-8018" data-mp="1d47cb072vfjo">
      <AnimatePresence initial={false} data-id="element-8019" data-mp="1n1lfvz7wk9uk">
        {show && <motion.div key="floating-bar" role={label ? 'region' : undefined} aria-label={label} initial={reduce ? {
        opacity: 0
      } : {
        y: '140%'
      }} animate={reduce ? {
        opacity: 1
      } : {
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        y: '140%'
      }} transition={{
        duration: 0.26,
        ease
      }} className="pointer-events-auto w-full max-w-lg rounded-[1.75rem] border border-line bg-shell/95 p-1.5 shadow-float backdrop-blur-md" data-id="element-8020" data-mp="1rstrn1wpg7aw">
            {children}
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/**
 * Ruimte onderaan de pagina, even hoog als de zwevende balk, zodat de footer (juridische links,
 * KvK) nooit onder de balk verdwijnt. Alleen op telefoon en tablet.
 */
export function FloatingBarSpacer() {
  return <div aria-hidden="true" className="h-24 shrink-0 lg:hidden" data-id="element-8021" data-mp="3f7aino51io1" />;
}